import {appLink, ISellerDetailParams} from "@rw/cars_routes";
import {Dispatch} from "redux";

import {apiLink} from "../../app/routes/api_link";
import {IOfferListApiResponse} from "../../app/routes/data_fetcher/app_path_data_fetcher";
import {IRouteState} from "../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../app/services/IServices";
import {appendQueryString} from "../../app/utils/append_query_string";
import {carsApiUrl} from "../../app/utils/read_environment_variables";
import {createRequestActionTypes} from "../../app/utils/request_response_utils/factories/create_request_action_types";
import {getRequest, postRequest} from "../../app/utils/request_response_utils/request";
import {enable301ResponseState, enable404ResponseState} from "../../app/utils/request_response_utils/response_state/response_state_actions";
import {ICarListOfferResponse} from "../../meta_data/reducers/meta_data_reducer";
import {getSellerSlug} from "../../offer/utils/get_seller_slug";

export const fetchSellerOffersTypes = createRequestActionTypes({view: "Seller", type: "GET", name: "SellerOffers"});

export interface ISellerOffersResult {
    slug: string;
    name: string;
    city?: string;
    wojew?: string;
    description?: string;
    address_street?: string;
    logo_url?: string;
    logo_key?: string;
    cover_url?: string;
    cover_key?: string;
    offers: ISellerOffer[];
}

export type ISellerOffer = ICarListOfferResponse & {
    acf: ICarListOfferResponse["acf"] & {
        sellers_name?: string;
        city?: string;
        wojew?: string;
        horse_power?: string;
        engine_size?: string;
        body_type?: string;
    };
};

type PublicSellerProfile = {
    display_name?: string;
    slug?: string;
    description?: string;
    address_street?: string;
    address_city?: string;
    address_voivodeship?: string;
    logo_url?: string;
    logo_key?: string;
    cover_url?: string;
    cover_key?: string;
    author_id?: number;
    logo?: string;
    avatar_url?: string;
    cover?: string;
};

const normalizePublicProfile = (raw: Record<string, any>): PublicSellerProfile => ({
    display_name: raw.display_name || raw.displayName || raw.name,
    slug: raw.slug,
    description: raw.description,
    address_street: raw.address_street || raw.addressStreet,
    address_city: raw.address_city || raw.addressCity || raw.city,
    address_voivodeship: raw.address_voivodeship || raw.addressVoivodeship || raw.voivodeship || raw.wojew,
    logo_url: raw.logo_url || raw.logoUrl || raw.logo || raw.avatar_url || raw.avatarUrl,
    logo_key: raw.logo_key || raw.logoKey,
    cover_url: raw.cover_url || raw.coverUrl || raw.cover || raw.background_url || raw.backgroundUrl,
    cover_key: raw.cover_key || raw.coverKey,
    author_id: raw.author_id != null ? Number(raw.author_id) : undefined
});

const MAX_PAGES = 8;
const PER_PAGE = 100;

const fetchActiveCarsPage = (services: Partial<IServices>, page: number) => {
    const url = appendQueryString(apiLink.cars.base({})(null), {
        offer_type: "osobowe",
        page: String(page),
        per_page: PER_PAGE,
        is_active: true,
        ss: "aB2a"
    });
    return getRequest(services, url, "fetchSellerOffers") as Promise<IOfferListApiResponse<ISellerOffer>>;
};

const fetchPublicSellerProfile = async (services: Partial<IServices>, slug: string): Promise<PublicSellerProfile | null> => {
    const base = (carsApiUrl || "").replace(/\/+$/, "");
    if (!base || !slug) {
        return null;
    }
    try {
        const url = `${base}/seller-by-slug/${encodeURIComponent(slug)}/`;
        const json = await getRequest(services, url, "fetchSellerProfileBySlug");
        const profile = json?.data && typeof json.data === "object" ? json.data : json;
        if (!profile || typeof profile !== "object" || json?.code === "not_found") {
            return null;
        }
        return normalizePublicProfile(profile as Record<string, any>);
    } catch {
        return null;
    }
};

/** cars-by-author omits `slug`; derive `offerSlug-id` from WP `link` path. */
const slugFromOfferLink = (link?: string | null): string | undefined => {
    if (!link || typeof link !== "string") {
        return undefined;
    }
    try {
        const path = new URL(link).pathname.replace(/\/+$/, "");
        const segment = path.split("/").filter(Boolean).pop();
        return segment || undefined;
    } catch {
        const cleaned = link.replace(/\/+$/, "");
        const segment = cleaned.split("/").filter(Boolean).pop();
        return segment || undefined;
    }
};

const normalizeAuthorOffer = (raw: Record<string, any>): ISellerOffer => {
    const id = Number(raw.id);
    const fromLink = slugFromOfferLink(raw.link);
    const slug = (typeof raw.slug === "string" && raw.slug) || fromLink || (Number.isFinite(id) ? String(id) : "");
    return {
        ...(raw as ISellerOffer),
        id,
        slug,
        acf: (raw.acf && typeof raw.acf === "object" ? raw.acf : {}) as ISellerOffer["acf"]
    };
};

const fetchOffersByAuthor = async (services: Partial<IServices>, authorId: number): Promise<ISellerOffer[]> => {
    const base = (carsApiUrl || "").replace(/\/+$/, "");
    if (!base || !authorId) {
        return [];
    }
    try {
        const json = await postRequest(services, `${base}/cars-by-author/`, {author_id: authorId}, "fetchSellerCarsByAuthor");
        const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
        return list.filter((item: unknown) => item && typeof item === "object").map((item: Record<string, any>) => normalizeAuthorOffer(item));
    } catch {
        return [];
    }
};

const fetchOfferSellerMeta = async (services: Partial<IServices>, carId: number): Promise<{seller_slug?: string; author_id?: number} | null> => {
    try {
        const url = appendQueryString(apiLink.cars.detail.base({})({carId}), {ss: "seller-meta"});
        const json = await getRequest(services, url, "fetchOfferSellerMeta");
        const data = json?.data;
        if (!data || typeof data !== "object") {
            return null;
        }
        return {
            seller_slug: data.seller_slug ? String(data.seller_slug) : undefined,
            author_id: data.author_id != null ? Number(data.author_id) : undefined
        };
    } catch {
        return null;
    }
};

const scanOffersBySellerNameSlug = async (services: Partial<IServices>, slug: string): Promise<ISellerOffer[]> => {
    const offers: ISellerOffer[] = [];
    let page = 1;
    let totalPages = 1;

    while (page <= totalPages && page <= MAX_PAGES) {
        const response = await fetchActiveCarsPage(services, page);
        const batch = response?.data || [];
        totalPages = response?.totalPages || 1;

        for (const car of batch) {
            if (getSellerSlug(car.acf?.sellers_name || "") === slug) {
                offers.push(car);
            }
        }
        page += 1;
    }
    return offers;
};

/**
 * Public seller landing.
 * Prefer WP profile slug (`seller_slug` / user nicename) + cars-by-author.
 * Fallback: match offers by slugified sellers_name, then resolve real seller_slug from cars-detail.
 */
export const fetchSellerOffersAtRoute =
    (services: Partial<IServices>, route: IRouteState<ISellerDetailParams>) =>
    async (dispatch: Dispatch): Promise<ISellerOffersResult | false> => {
        const routeSlug = (route.params.slug || "").trim().toLowerCase();
        dispatch({type: fetchSellerOffersTypes.start});

        if (!routeSlug) {
            dispatch(enable404ResponseState());
            return false;
        }

        try {
            let profile = await fetchPublicSellerProfile(services, routeSlug);
            let offers: ISellerOffer[] = [];

            if (profile?.author_id) {
                offers = await fetchOffersByAuthor(services, profile.author_id);
            }

            if (!offers.length) {
                offers = await scanOffersBySellerNameSlug(services, routeSlug);
            }

            // Name-based URL (e.g. radex-auto-komis) may differ from WP seller_slug (auto-komis-radex).
            const profileSlug = (profile?.slug || "").trim().toLowerCase();
            const needsCanonicalResolve =
                Boolean(offers.length) &&
                (!profile || (profileSlug && profileSlug !== routeSlug) || (!profile?.logo_url && !profile?.logo_key));

            if (needsCanonicalResolve) {
                const meta = await fetchOfferSellerMeta(services, offers[0].id);
                const canonicalSlug = meta?.seller_slug?.trim().toLowerCase();
                if (canonicalSlug && canonicalSlug !== routeSlug) {
                    const canonicalProfile = await fetchPublicSellerProfile(services, canonicalSlug);
                    if (canonicalProfile) {
                        profile = canonicalProfile;
                    }
                    if (meta.author_id) {
                        const byAuthor = await fetchOffersByAuthor(services, meta.author_id);
                        if (byAuthor.length) {
                            offers = byAuthor;
                        }
                    }
                    dispatch(enable301ResponseState(appLink.seller.detail({slug: canonicalSlug})));
                } else if (canonicalSlug && !profile) {
                    profile = await fetchPublicSellerProfile(services, canonicalSlug);
                    if (meta?.author_id) {
                        const byAuthor = await fetchOffersByAuthor(services, meta.author_id);
                        if (byAuthor.length) {
                            offers = byAuthor;
                        }
                    }
                } else if (meta?.author_id && !offers.length) {
                    offers = await fetchOffersByAuthor(services, meta.author_id);
                }
            }

            if (!offers.length) {
                dispatch(enable404ResponseState());
                return false;
            }

            const first = offers[0];
            const result: ISellerOffersResult = {
                slug: profile?.slug || routeSlug,
                name: profile?.display_name || first.acf?.sellers_name || routeSlug,
                city: profile?.address_city || first.acf?.city,
                wojew: profile?.address_voivodeship || first.acf?.wojew,
                description: profile?.description || undefined,
                address_street: profile?.address_street || undefined,
                logo_url: profile?.logo_url || undefined,
                logo_key: profile?.logo_key || undefined,
                cover_url: profile?.cover_url || undefined,
                cover_key: profile?.cover_key || undefined,
                offers
            };

            dispatch({type: fetchSellerOffersTypes.success, result});
            return result;
        } catch (err) {
            console.log("Error fetchSellerOffersAtRoute", err);
            dispatch(enable404ResponseState());
            return false;
        }
    };
